import * as React from "react";
import { CircleStop, Eraser, Gamepad2, LoaderCircle, Play, RotateCcw, TerminalSquare } from "lucide-react";
import type { AssetKind, CourseUnit, ProjectAssetState } from "./types";

type RunState = "loading" | "ready" | "running" | "stopped" | "error";
type ConsoleLine = { id: number; kind: "system" | "stdout" | "stderr"; text: string };
type DrawingCommand = Record<string, unknown> & { op: string };

const KEY_INDEX: Record<string, number> = {
  ArrowLeft: 0,
  ArrowRight: 1,
  ArrowUp: 2,
  ArrowDown: 3,
  " ": 4,
  a: 5,
  d: 6,
  w: 7,
  s: 8,
  Escape: 9,
};

function cssColor(value: unknown) {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    const [r = 255, g = 255, b = 255, a = 255] = value.map(Number);
    return `rgba(${r},${g},${b},${a / 255})`;
  }
  return "#ffffff";
}

function numbers(value: unknown, fallback: number[]) {
  if (!Array.isArray(value)) return fallback;
  return value.map((item) => Number(item));
}

function imageForSource(source: unknown, assets: ProjectAssetState) {
  const requested = String(source || "").toLowerCase().split(/[\\/]/).pop() || "";
  const entries = Object.entries(assets) as Array<[AssetKind, NonNullable<ProjectAssetState[AssetKind]>]>;
  const exact = entries.find(([, asset]) => asset.name.toLowerCase() === requested);
  if (exact) return exact[1].previewUrl;
  if (/hero|player|character|sprite/.test(requested)) return assets.hero?.previewUrl;
  if (/background|world|level|sky/.test(requested)) return assets.background?.previewUrl;
  if (/coin|star|goal|power|collect/.test(requested)) return assets.collectible?.previewUrl;
  return undefined;
}

function drawCommand(
  context: CanvasRenderingContext2D,
  command: DrawingCommand,
  assets: ProjectAssetState,
  imageCache: Map<string, HTMLImageElement>,
) {
  context.save();
  if (command.op === "clear") {
    context.fillStyle = cssColor(command.color);
    context.fillRect(0, 0, context.canvas.width, context.canvas.height);
  } else if (command.op === "rect") {
    const [x, y, width, height] = numbers(command.rect, [0, 0, 0, 0]);
    context.fillStyle = cssColor(command.color);
    context.strokeStyle = cssColor(command.color);
    context.lineWidth = Number(command.width || 1);
    if (Number(command.width || 0) > 0) context.strokeRect(x, y, width, height);
    else context.fillRect(x, y, width, height);
  } else if (command.op === "circle") {
    const [x, y] = numbers(command.center, [0, 0]);
    context.beginPath();
    context.arc(x, y, Number(command.radius || 0), 0, Math.PI * 2);
    context.fillStyle = cssColor(command.color);
    context.strokeStyle = cssColor(command.color);
    context.lineWidth = Number(command.width || 1);
    if (Number(command.width || 0) > 0) context.stroke();
    else context.fill();
  } else if (command.op === "line") {
    const [startX, startY] = numbers(command.start, [0, 0]);
    const [endX, endY] = numbers(command.end, [0, 0]);
    context.beginPath();
    context.moveTo(startX, startY);
    context.lineTo(endX, endY);
    context.strokeStyle = cssColor(command.color);
    context.lineWidth = Number(command.width || 1);
    context.stroke();
  } else if (command.op === "polygon") {
    const points = Array.isArray(command.points) ? command.points : [];
    if (points.length) {
      const [firstX, firstY] = numbers(points[0], [0, 0]);
      context.beginPath();
      context.moveTo(firstX, firstY);
      points.slice(1).forEach((point) => {
        const [x, y] = numbers(point, [0, 0]);
        context.lineTo(x, y);
      });
      context.closePath();
      context.fillStyle = cssColor(command.color);
      context.strokeStyle = cssColor(command.color);
      context.lineWidth = Number(command.width || 1);
      if (Number(command.width || 0) > 0) context.stroke();
      else context.fill();
    }
  } else if (command.op === "text") {
    const [x, y] = numbers(command.position, [0, 0]);
    const size = Math.max(10, Number(command.size || 24));
    context.font = `700 ${size}px "Atkinson Hyperlegible", sans-serif`;
    context.textBaseline = "top";
    context.fillStyle = cssColor(command.color);
    context.fillText(String(command.text || ""), x, y);
  } else if (command.op === "image") {
    const imageUrl = imageForSource(command.source, assets);
    if (imageUrl) {
      const [x, y] = numbers(command.position, [0, 0]);
      const [width, height] = numbers(command.size, [64, 64]);
      let image = imageCache.get(imageUrl);
      if (!image) {
        image = new Image();
        image.src = imageUrl;
        imageCache.set(imageUrl, image);
      }
      if (image.complete) context.drawImage(image, x, y, width, height);
    }
  }
  context.restore();
}

export function PythonStudio({
  unit,
  code,
  setCode,
  assets,
}: {
  unit: CourseUnit;
  code: string;
  setCode: (code: string) => void;
  assets: ProjectAssetState;
}) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const editorRef = React.useRef<HTMLTextAreaElement>(null);
  const workerRef = React.useRef<Worker | null>(null);
  const keyBufferRef = React.useRef<SharedArrayBuffer | null>(null);
  const stopTimerRef = React.useRef<number | null>(null);
  const consoleEndRef = React.useRef<HTMLDivElement>(null);
  const imageCacheRef = React.useRef(new Map<string, HTMLImageElement>());
  const commandQueueRef = React.useRef<DrawingCommand[]>([]);
  const lineId = React.useRef(0);
  const [runState, setRunState] = React.useState<RunState>("loading");
  const [pythonVersion, setPythonVersion] = React.useState("3.12");
  const [caption, setCaption] = React.useState("Python game window");
  const [consoleLines, setConsoleLines] = React.useState<ConsoleLine[]>([
    { id: 0, kind: "system", text: "Starting the Python 3.12 classroom runtime…" },
  ]);

  const addConsole = React.useCallback((kind: ConsoleLine["kind"], text: string) => {
    if (!text) return;
    setConsoleLines((current) => [...current.slice(-249), { id: ++lineId.current, kind, text }]);
  }, []);

  const renderFrame = React.useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    const commands = commandQueueRef.current.splice(0);
    commands.forEach((command) => drawCommand(context, command, assets, imageCacheRef.current));
  }, [assets]);

  const createWorker = React.useCallback(() => {
    workerRef.current?.terminate();
    const worker = new Worker("/python-worker.mjs", { type: "module" });
    workerRef.current = worker;
    setRunState("loading");
    addConsole("system", "Loading CPython and the Pygame classroom canvas…");
    worker.onmessage = (event: MessageEvent<Record<string, unknown>>) => {
      const message = event.data;
      if (message.type === "ready") {
        setPythonVersion(String(message.pythonVersion || "3.12"));
        setRunState("ready");
        addConsole("system", `Python ${String(message.pythonVersion || "3.12")} ready. Press Run.`);
      } else if (message.type === "running") {
        setRunState("running");
      } else if (message.type === "stdout") {
        addConsole("stdout", String(message.text || ""));
      } else if (message.type === "stderr") {
        addConsole("stderr", String(message.text || ""));
      } else if (message.type === "package") {
        addConsole("system", String(message.text || ""));
      } else if (message.type === "draw") {
        const command = message.command as DrawingCommand;
        if (command.op === "resize") {
          const canvas = canvasRef.current;
          if (canvas) {
            canvas.width = Math.max(160, Math.min(1280, Number(command.width || 800)));
            canvas.height = Math.max(120, Math.min(900, Number(command.height || 500)));
          }
          commandQueueRef.current = [];
        } else if (command.op === "caption") {
          setCaption(String(command.title || "Python game window"));
        } else if (command.op === "frame") {
          renderFrame();
        } else {
          commandQueueRef.current.push(command);
        }
      } else if (message.type === "done") {
        renderFrame();
        setRunState("ready");
        addConsole("system", "Program finished.");
      } else if (message.type === "stopped") {
        setRunState("stopped");
        addConsole("system", "Program stopped.");
      } else if (message.type === "error") {
        setRunState("error");
        addConsole("stderr", String(message.text || "Python stopped with an error."));
      }
    };
    worker.onerror = (event) => {
      setRunState("error");
      addConsole("stderr", event.message || "The Python worker could not start.");
    };
  }, [addConsole, renderFrame]);

  React.useEffect(() => {
    createWorker();
    return () => {
      workerRef.current?.terminate();
      if (stopTimerRef.current) window.clearTimeout(stopTimerRef.current);
    };
  }, [createWorker]);

  React.useEffect(() => {
    consoleEndRef.current?.scrollIntoView({ block: "nearest" });
  }, [consoleLines]);

  React.useEffect(() => {
    const update = (event: KeyboardEvent, pressed: boolean) => {
      const index = KEY_INDEX[event.key.length === 1 ? event.key.toLowerCase() : event.key];
      const buffer = keyBufferRef.current;
      if (index === undefined || runState !== "running") return;
      event.preventDefault();
      if (buffer) Atomics.store(new Int32Array(buffer), index + 1, pressed ? 1 : 0);
      else workerRef.current?.postMessage({ type: "key", index, pressed });
    };
    const down = (event: KeyboardEvent) => update(event, true);
    const up = (event: KeyboardEvent) => update(event, false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [runState]);

  function run() {
    if (runState === "loading" || runState === "running") return;
    if (!code.trim()) {
      addConsole("stderr", "main.py is blank. Type the teacher's example, then press Run.");
      return;
    }
    if (runState === "error" || runState === "stopped") createWorker();
    const worker = workerRef.current;
    if (!worker) return;
    setConsoleLines([{ id: ++lineId.current, kind: "system", text: `Running Unit ${unit.week} main.py…` }]);
    commandQueueRef.current = [];
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (canvas && context) {
      context.fillStyle = "#101a2f";
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    let keyBuffer: SharedArrayBuffer | null = null;
    if (typeof SharedArrayBuffer !== "undefined" && window.crossOriginIsolated) {
      keyBuffer = new SharedArrayBuffer(Int32Array.BYTES_PER_ELEMENT * 64);
    }
    keyBufferRef.current = keyBuffer;
    const start = () => worker.postMessage({ type: "run", code, keyBuffer });
    if (runState === "error" || runState === "stopped") window.setTimeout(start, 1800);
    else start();
  }

  function stop() {
    const buffer = keyBufferRef.current;
    if (buffer) Atomics.store(new Int32Array(buffer), 0, 2);
    setRunState("stopped");
    addConsole("system", "Stopping program…");
    stopTimerRef.current = window.setTimeout(() => {
      workerRef.current?.terminate();
      createWorker();
    }, 350);
  }

  function resetRuntime() {
    commandQueueRef.current = [];
    setConsoleLines([{ id: ++lineId.current, kind: "system", text: "Python runtime reset." }]);
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (canvas && context) {
      context.fillStyle = "#101a2f";
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    createWorker();
  }

  function insertTab(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== "Tab") return;
    event.preventDefault();
    const editor = event.currentTarget;
    const start = editor.selectionStart;
    const end = editor.selectionEnd;
    setCode(`${code.slice(0, start)}    ${code.slice(end)}`);
    window.requestAnimationFrame(() => editorRef.current?.setSelectionRange(start + 4, start + 4));
  }

  return (
    <div className="python-studio">
      <section className="idle-editor">
        <header>
          <div><p>PYTHON IDLE-STYLE EDITOR</p><strong>main.py</strong></div>
          <span>CPython {pythonVersion} · private file</span>
        </header>
        <textarea
          ref={editorRef}
          aria-label="Python game code"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          onKeyDown={insertTab}
          spellCheck={false}
          placeholder="# Type the code your teacher shows on the smartboard.\nimport pygame\n\npygame.init()"
        />
        <footer>
          <p>Unit {unit.week}: {unit.gameFeature}. Your code saves automatically.</p>
          <div className="run-controls">
            <button className="button mint" disabled={runState === "loading" || runState === "running"} onClick={run}>
              {runState === "loading" ? <LoaderCircle className="spin" /> : <Play />} Run
            </button>
            <button className="button stop-button" disabled={runState !== "running"} onClick={stop}><CircleStop /> Stop</button>
            <button className="button dark-secondary" onClick={resetRuntime}><RotateCcw /> Restart shell</button>
          </div>
        </footer>
      </section>
      <section className="runtime-stack">
        <div className="game-window">
          <header><Gamepad2 /><strong>{caption}</strong><span>Arrow keys or WASD</span></header>
          <div className="canvas-wrap"><canvas ref={canvasRef} width="800" height="500" tabIndex={0} /></div>
        </div>
        <div className="python-console">
          <header><TerminalSquare /><strong>Python Shell</strong><button onClick={() => setConsoleLines([])}><Eraser /> Clear</button></header>
          <div className="console-output" aria-live="polite">
            {consoleLines.map((line) => <pre className={line.kind} key={line.id}>{line.kind === "stdout" ? ">>> " : ""}{line.text}</pre>)}
            <div ref={consoleEndRef} />
          </div>
        </div>
      </section>
    </div>
  );
}
